import Checkbox from '@rio-cloud/rio-uikit/components/checkbox/Checkbox';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import './styles.css';

export const columns = [
    { key: 'id', fixed: true },
    { key: 'client' },
    { key: 'vehicle' },
    { key: 'chassis' },
    { key: 'repair' },
    { key: 'serviceType' },
    { key: 'owner' },
    { key: 'assignedTo' },
    { key: 'stage' },
    { key: 'regional' },
    { key: 'sg' },
    { key: 'repairDate' },
    { key: 'stageTime' },
    { key: 'saleDate' },
    { key: 'km' },
    { key: 'warrantyType' },
    { key: 'dealerDate' },
] as const;

export type ColumnKey = (typeof columns)[number]['key'];

export const defaultVisibleColumns: ColumnKey[] = [
    'client',
    'vehicle',
    'repair',
    'owner',
    'assignedTo',
    'stage',
    'dealerDate',
];

type ColumnSelectorProps = {
    selectedColumns: ColumnKey[];
    onChange: (columns: ColumnKey[]) => void;
};

export default function ColumnSelector({ selectedColumns, onChange }: ColumnSelectorProps) {
    const intl = useIntl();
    const rootRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const selected = new Set(selectedColumns);

    useEffect(() => {
        const close = (event: MouseEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    const toggle = (key: ColumnKey) => {
        const next = new Set(selected);
        if (next.has(key)) {
            next.delete(key);
        } else {
            next.add(key);
        }
        onChange(columns.filter(column => next.has(column.key)).map(column => column.key));
    };

    return (
        <div className='aeg-column-selector position-relative' ref={rootRef}>
            <button
                aria-expanded={open}
                aria-haspopup='menu'
                className='btn btn-default'
                onClick={() => setOpen(current => !current)}
                type='button'
            >
                {intl.formatMessage({ id: 'intl-msg:support.aeg.columns.button' })} ▾
            </button>
            {open ? (
                <div className='aeg-column-selector-menu position-absolute right-0 bg-white border rounded shadow-default'>
                    <header className='display-flex align-items-center justify-content-between padding-15 border border-top-none border-left-none border-right-none'>
                        <strong className='text-color-darkest'>{intl.formatMessage({ id: 'intl-msg:support.aeg.columns.shown' })}</strong>
                        <span className='text-color-dark'>
                            {intl.formatMessage({ id: 'intl-msg:support.aeg.columns.count' }, { selected: selected.size, total: columns.length - 1 })}
                        </span>
                    </header>
                    <div className='aeg-column-selector-options overflow-auto padding-10'>
                        {columns.map(column => {
                            const fixed = 'fixed' in column && column.fixed;
                            const checked = fixed || selected.has(column.key);
                            return (
                                <label
                                    className='display-flex align-items-center gap-10 padding-10 margin-0 cursor-pointer text-color-darker'
                                    key={column.key}
                                >
                                    <Checkbox checked={checked} disabled={fixed} onChange={() => toggle(column.key)} />
                                    <span>{intl.formatMessage({ id: `intl-msg:support.aeg.columns.${column.key}` })}</span>
                                    {fixed ? <small className='margin-left-auto text-color-dark'>{intl.formatMessage({ id: 'intl-msg:support.aeg.columns.fixed' })}</small> : null}
                                </label>
                            );
                        })}
                    </div>
                    <footer className='padding-10 text-right border border-bottom-none border-left-none border-right-none'>
                        <button
                            className='btn btn-link text-color-highlight-dark'
                            onClick={() => onChange(defaultVisibleColumns)}
                            type='button'
                        >
                            {intl.formatMessage({ id: 'intl-msg:support.aeg.columns.reset' })}
                        </button>
                    </footer>
                </div>
            ) : null}
        </div>
    );
}
